// Layered decorative graphics for the Northwest theme.
// All stay within the fog-white / deep-navy / steel-blue palette.
const TOPO_BASE_AMPLITUDE = 22;
const TOPO_AMPLITUDE_DECAY = 1.6;

export const TopoLines = ({ className = '', opacity = 0.5 }) => (
  <svg
    className={className}
    viewBox="0 0 1200 400"
    preserveAspectRatio="none"
    aria-hidden
    style={{ opacity }}
  >
    <defs>
      <linearGradient id="topoFade" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#748CAB" stopOpacity="0" />
        <stop offset="50%" stopColor="#748CAB" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#748CAB" stopOpacity="0" />
      </linearGradient>
    </defs>
    {Array.from({ length: 9 }).map((_, i) => {
      const y = 40 + i * 40;
      const amp = TOPO_BASE_AMPLITUDE - i * TOPO_AMPLITUDE_DECAY;
      const d = `M0 ${y} C 150 ${y - amp}, 300 ${y + amp}, 450 ${y - amp * 0.6} S 750 ${y + amp}, 900 ${y - amp * 0.5} S 1200 ${y + amp * 0.4}, 1200 ${y}`;
      return (
        <path
          key={`topo-${y}`}
          d={d}
          fill="none"
          stroke="url(#topoFade)"
          strokeWidth="1"
        />
      );
    })}
  </svg>
);

export const CircuitAccent = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 320 200"
    aria-hidden
  >
    <g stroke="#3E5C76" strokeWidth="1" fill="none" opacity="0.55">
      <path d="M10 100 L80 100 L100 80 L180 80 L200 100 L310 100" />
      <path d="M10 140 L60 140 L90 170 L200 170" />
      <path d="M40 30 L40 70 L120 70" />
      <path d="M240 30 L240 60 L200 60 L180 80" />
    </g>
    <g fill="#0B2545">
      <circle cx="80" cy="100" r="3" />
      <circle cx="180" cy="80" r="3" />
      <circle cx="200" cy="170" r="3" />
      <circle cx="40" cy="30" r="3" />
      <circle cx="240" cy="30" r="3" />
    </g>
    <g fill="#8DA9C4">
      <circle cx="310" cy="100" r="2" />
      <circle cx="120" cy="70" r="2" />
      <circle cx="60" cy="140" r="2" />
    </g>
  </svg>
);

export const LayeredMountains = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 1440 480"
    preserveAspectRatio="none"
    aria-hidden
  >
    <defs>
      <linearGradient id="m1" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#DDE5EE" />
        <stop offset="100%" stopColor="#F1F4F8" />
      </linearGradient>
      <linearGradient id="m2" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#B8C7D6" />
        <stop offset="100%" stopColor="#DDE5EE" />
      </linearGradient>
      <linearGradient id="m3" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#8DA9C4" />
        <stop offset="100%" stopColor="#B8C7D6" />
      </linearGradient>
      <linearGradient id="m4" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#3E5C76" />
        <stop offset="100%" stopColor="#748CAB" />
      </linearGradient>
    </defs>
    {/* Far range */}
    <path d="M0 300 L120 250 L200 280 L300 220 L420 270 L540 200 L660 260 L780 210 L900 265 L1020 220 L1140 275 L1260 230 L1440 285 L1440 480 L0 480 Z" fill="url(#m1)" opacity="0.8"/>
    {/* Mid range */}
    <path d="M0 340 L100 310 L200 340 L340 260 L460 320 L580 260 L720 320 L860 240 L980 310 L1100 260 L1240 320 L1360 280 L1440 320 L1440 480 L0 480 Z" fill="url(#m2)" opacity="0.9"/>
    {/* Near range with snow caps */}
    <path d="M0 400 L120 350 L220 380 L340 300 L460 360 L560 320 L700 380 L820 300 L940 370 L1060 320 L1200 380 L1320 340 L1440 380 L1440 480 L0 480 Z" fill="url(#m3)"/>
    {/* Snow caps */}
    <g fill="#F6F8FA" opacity="0.85">
      <path d="M340 300 L360 315 L330 320 L310 310 Z" />
      <path d="M820 300 L845 316 L810 322 L790 312 Z" />
      <path d="M560 320 L578 330 L550 335 L540 328 Z" />
    </g>
    {/* Foreground darker range */}
    <path d="M0 440 L120 410 L260 430 L400 380 L520 420 L660 380 L800 430 L940 380 L1080 425 L1220 390 L1360 430 L1440 410 L1440 480 L0 480 Z" fill="url(#m4)" opacity="0.75"/>
  </svg>
);

export const WashingtonOutline = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 500 300"
    aria-hidden
  >
    {/* Stylized WA state silhouette (approximation) */}
    <path
      d="M20 60 L60 55 L110 62 L160 58 L210 55 L260 60 L305 55 L350 60 L400 55 L460 60 L470 90 L475 130 L470 170 L460 210 L440 240 L400 250 L360 245 L320 250 L280 245 L240 250 L200 245 L165 250 L130 245 L95 250 L65 240 L45 220 L30 190 L22 155 L18 120 L20 60 Z"
      fill="none"
      stroke="#3E5C76"
      strokeWidth="1.5"
      strokeLinejoin="round"
      opacity="0.35"
    />
    {/* Puget Sound suggestion */}
    <path
      d="M110 130 C 118 155, 128 175, 140 195 M100 90 C 108 110, 112 125, 108 145"
      fill="none"
      stroke="#748CAB"
      strokeWidth="1"
      opacity="0.4"
    />
  </svg>
);

export const CompassAccent = ({ className = '', size = 40 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 40 40"
    aria-hidden
  >
    <circle cx="20" cy="20" r="18" fill="none" stroke="#3E5C76" strokeWidth="0.8" opacity="0.6" />
    <circle cx="20" cy="20" r="12" fill="none" stroke="#3E5C76" strokeWidth="0.5" opacity="0.35" strokeDasharray="2 2" />
    <path d="M20 5 L23 20 L20 35 L17 20 Z" fill="#0B2545" opacity="0.7" />
    <path d="M5 20 L20 17 L35 20 L20 23 Z" fill="#8DA9C4" opacity="0.6" />
    <circle cx="20" cy="20" r="1.5" fill="#0B2545" />
  </svg>
);
